import React, { useState, useEffect, useMemo, useCallback } from 'react'
import {
  Card,
  CardContent,
  Typography,
  Grid,
  Tabs,
  Tab,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  LinearProgress,
  CircularProgress
} from '@material-ui/core'
import {
  Add as AddIcon,
  Refresh as RefreshIcon,
  Settings as SettingsIcon,
  Assessment as AssessmentIcon,
  History as HistoryIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Cancel as CancelIcon,
  Notifications as NotificationsIcon
} from '@material-ui/icons'
import { TourProvider, useTourContext } from '../contexts/TourContext'
import { tourApi } from '../api/tourApi'
import TourProcessingOptimized from '../components/TourProcessingOptimized'
import TourList from '../components/TourList'
import TourStats from '../components/TourStats'
import TourHistory from '../components/TourHistory'
import TourDetailsDialog from '../components/TourDetailsDialog'
import { useTourNotifications, TourNotificationContainer } from '../components/TourNotification'
import moment from 'moment'

// Estilos inline para la vista principal de gestión de tours

/**
 * Vista principal de gestión de tours optimizada
 * Implementa todas las mejoras identificadas en el análisis
 */
const TourManagementView = () => {
  const [activeTab, setActiveTab] = useState(0)
  const [selectedTour, setSelectedTour] = useState(null)
  const [processingDialog, setProcessingDialog] = useState(false)
  const [statsDialog, setStatsDialog] = useState(false)
  const [historyDialog, setHistoryDialog] = useState(false)
  const [cancelDialog, setCancelDialog] = useState(false)
  const [tourToCancel, setTourToCancel] = useState(null)
  const [detailsDialog, setDetailsDialog] = useState(false)
  const [selectedTourId, setSelectedTourId] = useState(null)
  const [stats, setStats] = useState(null)
  const [loadingStats, setLoadingStats] = useState(false)
  const [globalLoading, setGlobalLoading] = useState(false)

  // Hook de notificaciones
  const {
    notifications,
    removeNotification,
    showSuccess,
    showError,
    showWarning,
    showInfo
  } = useTourNotifications()

  // Cargar estadísticas
  const loadStats = async () => {
    setLoadingStats(true)
    try {
      const response = await tourApi.getTourStats()
      setStats(response.data)
      //showSuccess('Estadísticas actualizadas correctamente', 'Éxito')
    } catch (error) {
      console.error('Error loading stats:', error)
      //showError('Error al cargar las estadísticas', 'Error')
    } finally {
      setLoadingStats(false)
    }
  }

  // Cargar estadísticas al montar
  useEffect(() => {
    loadStats()
  }, [])

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue)
  }

  const handleProcessTour = (tour) => {
    setSelectedTour(tour)
    setProcessingDialog(true)
    //showInfo(`Procesando tour #${tour.id}`, 'Procesando')
  }

  const handleCloseProcessing = () => {
    setProcessingDialog(false)
    setSelectedTour(null)
  }

  const handleProcessSuccess = (result) => {
    handleCloseProcessing()
    //showSuccess(`Tour #${selectedTour && selectedTour.id} procesado exitosamente`, 'Éxito')
    // Recargar datos después del procesamiento
    loadStats()
  }

  const handleProcessError = (error) => {
    console.error('Error procesando tour:', error)
    //showError(`Error al procesar tour: ${error.message}`, 'Error')
  }

  const handleViewTour = (tour) => {
    // Abrir diálogo de detalles del tour
    setSelectedTourId(getNestedValue(tour, 'id'))
    setDetailsDialog(true)
   // showInfo(`Viendo detalles del tour #${getNestedValue(tour, 'id')}`, 'Información')
  }

  const handleEditTour = (tour) => {
    // Implementar edición del tour
    console.log('Editar tour:', tour)
    //showWarning('Función de edición en desarrollo', 'En desarrollo')
  }

  const handleDeleteTour = (tour) => {
    // Abrir diálogo de confirmación para cancelar tour
    setTourToCancel(tour)
    setCancelDialog(true)
    //showWarning(`Preparando cancelación del tour #${getNestedValue(tour, 'id')}`, 'Advertencia')
  }

  const handleConfirmCancel = async () => {
    if (!tourToCancel) return

    setGlobalLoading(true)
    try {
      // Llamar al API para cancelar el tour
      const result = await tourApi.cancelTour(tourToCancel.id)
      
      if (result.success) {
        showSuccess(`Tour #${tourToCancel.id} cancelled successfully`, 'Success')
        
        // Recargar estadísticas
        loadStats()
        
        // Cerrar diálogo
        setCancelDialog(false)
        setTourToCancel(null)
      } else {
        showError(`Error cancelling tour: ${result.error}`, 'Error')
      }
    } catch (error) {
      console.error('Error cancelling tour:', error)
      showError(`Error cancelling tour: ${error.message}`, 'Error')
    } finally {
      setGlobalLoading(false)
    }
  }

  const handleCancelDialogClose = () => {
    setCancelDialog(false)
    setTourToCancel(null)
  }

  const handleCloseDetails = () => {
    setDetailsDialog(false)
    setSelectedTourId(null)
  }

  const getNestedValue = (obj, path, defaultValue = 'N/A') => {
    if (!obj) return defaultValue
    const keys = path.split('.')
    let current = obj
    for (const key of keys) {
      if (current && typeof current === 'object' && key in current) {
        current = current[key]
      } else {
        return defaultValue
      }
    }
    return current || defaultValue
  }

  return (
    <TourProvider>
      <div style={{ backgroundColor: '#fafafa', minHeight: '100vh', padding: 24 }}>
        {/* Overlay de carga global */}
        {globalLoading && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(255,255,255,0.8)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999
          }}>
            <div style={{ textAlign: 'center' }}>
              <CircularProgress size={60} />
              <Typography variant="h6" style={{ marginTop: 16 }}>
                Processing...
              </Typography>
            </div>
          </div>
        )}

        {/* Header mejorado */}
  {/*       <Card style={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          borderRadius: 16,
          padding: 32,
          marginBottom: 32,
          color: 'white',
          position: 'relative',
          overflow: 'hidden'
        }}>
          <CardContent style={{ position: 'relative', zIndex: 1 }}>
            <Grid container spacing={24} alignItems="center">
              <Grid item xs={12} md={8}>
                                  <Typography variant="h3" gutterBottom style={{ fontWeight: 700 }}>
                    Tour Management
                  </Typography>
                  <Typography variant="h6" style={{ opacity: 0.9, marginBottom: 16 }}>
                    Manage and monitor your tour bookings efficiently
                  </Typography>
                <div style={{ display: 'flex', gap: 16 }}>
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<RefreshIcon />}
                    onClick={loadStats}
                    disabled={loadingStats}
                    style={{ 
                      borderRadius: 25,
                      padding: '8px 24px',
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                      transition: 'all 0.3s ease-in-out',
                      backgroundColor: 'rgba(255,255,255,0.2)', 
                      color: 'white' 
                    }}
                  >
                    Refresh
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<AssessmentIcon />}
                    onClick={() => setStatsDialog(true)}
                    style={{ 
                      borderRadius: 25,
                      padding: '8px 24px',
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                      transition: 'all 0.3s ease-in-out',
                      borderColor: 'rgba(255,255,255,0.5)', 
                      color: 'white' 
                    }}
                  >
                    Statistics
                  </Button>
                </div>
              </Grid>
              <Grid item xs={12} md={4}>
                <div style={{ textAlign: 'center' }}>
                  <NotificationsIcon style={{ fontSize: 64, opacity: 0.3 }} />
                                     <Typography variant="h4" style={{ fontWeight: 600, marginTop: 8 }}>
                     {stats && stats.total || 0}
                   </Typography>
                  <Typography variant="body1" style={{ opacity: 0.8 }}>
                    Total Tours
                  </Typography>
                </div>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
 */}
  {/* Tabs mejorados */}
       {/*  <Card style={{
          backgroundColor: 'white',
          borderRadius: 12,
          boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
          marginBottom: 24
        }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            indicatorColor="primary"
            textColor="primary"
            centered
          >
            <Tab 
              label="PENDING TOURS" 
              icon={<WarningIcon />}
              iconPosition="start"
            />
            <Tab 
              label="PROCESSED TOURS" 
              icon={<CheckCircleIcon />}
              iconPosition="start"
            />
            <Tab 
              label="HISTORY" 
              icon={<HistoryIcon />}
              iconPosition="start"
            />
          </Tabs>
        </Card> */}

        {/* Content based on active tab */}
        {activeTab === 0 && (
          <TourList
            showPending={true}
            onProcessTour={handleProcessTour}
            onViewTour={handleViewTour}
            onEditTour={handleEditTour}
            onDeleteTour={handleDeleteTour}
          />
        )}

        {activeTab === 1 && (
          <TourList
            showPending={false}
            onProcessTour={handleProcessTour}
            onViewTour={handleViewTour}
            onEditTour={handleEditTour}
            onDeleteTour={handleDeleteTour}
          />
        )}

        {activeTab === 2 && <HistoryTab />}

        {/* Dialogs */}
        <ProcessingDialog
          open={processingDialog}
          tour={selectedTour}
          onClose={handleCloseProcessing}
          onSuccess={handleProcessSuccess}
          onError={handleProcessError}
        />

        <StatsDialog
          open={statsDialog}
          onClose={() => setStatsDialog(false)}
        />

        <HistoryDialog
          open={historyDialog}
          onClose={() => setHistoryDialog(false)}
        />

        {/* Cancel Tour Confirmation Dialog */}
        <CancelTourDialog
          open={cancelDialog}
          tour={tourToCancel}
          onClose={handleCancelDialogClose}
          onConfirm={handleConfirmCancel}
        />

        {/* Tour Details Dialog */}
        <TourDetailsDialog
          open={detailsDialog}
          tourId={selectedTourId}
          onClose={handleCloseDetails}
          onProcess={handleProcessTour}
          onEdit={handleEditTour}
          onCancel={handleDeleteTour}
        />

        {/* Notificaciones */}
        <TourNotificationContainer 
          notifications={notifications}
          onRemove={removeNotification}
        />
      </div>
    </TourProvider>
  )
}

/**
 * Componente de estadísticas rápidas
 */
const QuickStats = ({ stats, loading }) => {
  if (loading) {
    return (
      <Card>
        <CardContent>
          <LinearProgress />
        </CardContent>
      </Card>
    )
  }

  if (!stats) {
    return null
  }

  return (
            <Grid container spacing={24}>
      <Grid item xs={12} sm={6} md={3}>
        <StatCard
          title="Total Tours"
          value={stats.total || 0}
          color="primary"
          icon={<AssessmentIcon />}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <StatCard
          title="Pending"
          value={stats.pending || 0}
          color="warning"
          icon={<WarningIcon />}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <StatCard
          title="Processed"
          value={stats.processed || 0}
          color="success"
          icon={<CheckCircleIcon />}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <StatCard
          title="Cancelled"
          value={stats.cancelled || 0}
          color="error"
          icon={<ErrorIcon />}
        />
      </Grid>
    </Grid>
  )
}

/**
 * Tarjeta de estadística individual
 */
const StatCard = ({ title, value, color, icon }) => (
  <Card>
    <CardContent>
      <Grid container spacing={2} alignItems="center">
        <Grid item>
          <div style={{ color: color === 'primary' ? '#1976d2' : 
                              color === 'warning' ? '#ff9800' :
                              color === 'success' ? '#4caf50' :
                              color === 'error' ? '#f44336' : '#757575' }}>
            {icon}
          </div>
        </Grid>
        <Grid item xs>
          <Typography variant="h4" component="div">
            {value}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            {title}
          </Typography>
        </Grid>
      </Grid>
    </CardContent>
  </Card>
)

/**
 * Pestaña de historial
 */
const HistoryTab = () => (
  <Card style={{ borderRadius: 16, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
    <CardContent>
      <Typography variant="h6" gutterBottom style={{ color: '#2c3e50', fontWeight: 600 }}>
        Tour History
      </Typography>
      <Typography color="textSecondary">
        History functionality in development...
      </Typography>
    </CardContent>
  </Card>
)

/**
 * Botones de acción
 */
const ActionButtons = ({ onShowStats, onShowHistory }) => (
  <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
  {/*   <Button
      variant="outlined"
      startIcon={<AssessmentIcon />}
      onClick={onShowStats}
    >
      Estadísticas
    </Button> */}
  {/*   <Button
      variant="outlined"
      startIcon={<HistoryIcon />}
      onClick={onShowHistory}
    >
      Historial
    </Button> */}
  </div>
)

/**
 * Diálogo de procesamiento de tour
 */
const ProcessingDialog = ({ open, tour, onClose, onSuccess, onError }) => {
  if (!tour) return null

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Process Tour #{tour.id}
      </DialogTitle>
      <DialogContent>
        <TourProcessingOptimized 
          tour={tour}
          onSuccess={onSuccess}
          onError={onError}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary">
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  )
}

/**
 * Diálogo de estadísticas
 */
const StatsDialog = ({ open, onClose }) => (
  <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
          <DialogTitle>
        Detailed Statistics
      </DialogTitle>
    <DialogContent>
      <TourStats />
    </DialogContent>
    <DialogActions>
      <Button onClick={onClose} color="primary">
        Close
      </Button>
    </DialogActions>
  </Dialog>
)

/**
 * Diálogo de historial
 */
const HistoryDialog = ({ open, onClose }) => (
  <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
          <DialogTitle>
        Tour History
      </DialogTitle>
    <DialogContent>
      <TourHistory />
    </DialogContent>
    <DialogActions>
      <Button onClick={onClose} color="primary">
        Close
      </Button>
    </DialogActions>
  </Dialog>
)

/**
 * Cancel tour confirmation dialog
 */
const CancelTourDialog = ({ open, tour, onClose, onConfirm }) => {
  const getNestedValue = (obj, path, defaultValue = 'N/A') => {
    if (!obj) return defaultValue
    const keys = path.split('.')
    let current = obj
    for (const key of keys) {
      if (current && typeof current === 'object' && key in current) {
        current = current[key]
      } else {
        return defaultValue
      }
    }
    return current || defaultValue
  }

  const tourName = getNestedValue(tour, 'tour_info.tour_name')
  const clientName = getNestedValue(tour, 'client_info.name')
  const tourStartDate = getNestedValue(tour, 'tour_info.tour_start_date')

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle style={{ color: '#d32f2f' }}>
        Cancel Book Tour
      </DialogTitle>
      <DialogContent>
        <Typography variant="body1" gutterBottom>
          Are you sure you want to cancel this tour booking?
        </Typography>
        
        {tour && (
          <div style={{ marginTop: 16, padding: 16, backgroundColor: '#f5f5f5', borderRadius: 8 }}>
            <Typography variant="subtitle2" gutterBottom>
              Tour Details:
            </Typography>
            <Typography variant="body2" color="textSecondary">
              <strong>Tour Rent ID:</strong> {getNestedValue(tour, 'id')}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              <strong>Tour Name:</strong> {tourName}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              <strong>Client:</strong> {clientName}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              <strong>Start Date:</strong> {tourStartDate ? moment(tourStartDate).format('DD/MM/YYYY') : 'N/A'}
            </Typography>
          </div>
        )}
        
        <Typography variant="body2" color="error" style={{ marginTop: 16 }}>
          ⚠️ This action cannot be undone. The tour will be permanently cancelled.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary">
          Keep Tour
        </Button>
        <Button 
          onClick={onConfirm} 
          color="error" 
          variant="contained"
          startIcon={<CancelIcon />}
        >
          Cancel Book Tour
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default TourManagementView 


// WEBPACK FOOTER //
// ./src/features/tours/views/TourManagementView.js